import { createContext, useContext, useMemo } from 'react';
import { useCentralizedProviderData } from '@crehana/auth';
import { jsx as _jsx } from "react/jsx-runtime";
var Context = /*#__PURE__*/createContext(undefined);

/**
 * Provider that wraps the application and provides the application roles, products and permissions.
 * It will use the props passed to the provider if they are defined, otherwise it will use the values from the centralized provider.
 */
export var RBACProvider = function RBACProvider(_ref) {
  var children = _ref.children,
    applicationRolesProps = _ref.applicationRoles,
    applicationProductsProps = _ref.applicationProducts,
    applicationPermissionsProps = _ref.applicationPermissions;
  var _useCentralizedProvid = useCentralizedProviderData(),
    me = _useCentralizedProvid.me;
  var applicationRoles = useMemo(function () {
    var _me$security;
    return applicationRolesProps || (me === null || me === void 0 ? void 0 : (_me$security = me.security) === null || _me$security === void 0 ? void 0 : _me$security.applicationRoles) || [];
  }, [applicationRolesProps, me]);
  var applicationProducts = useMemo(function () {
    var _me$security2;
    return applicationProductsProps || (me === null || me === void 0 ? void 0 : (_me$security2 = me.security) === null || _me$security2 === void 0 ? void 0 : _me$security2.applicationProducts) || [];
  }, [applicationProductsProps, me]);
  var applicationPermissions = useMemo(function () {
    var _me$security3;
    return applicationPermissionsProps || (me === null || me === void 0 ? void 0 : (_me$security3 = me.security) === null || _me$security3 === void 0 ? void 0 : _me$security3.applicationPermissions) || [];
  }, [applicationPermissionsProps, me]);

  // Optimized Sets for O(1) lookups
  var applicationRolesSet = useMemo(function () {
    return new Set(applicationRoles);
  }, [applicationRoles]);
  var applicationProductsSet = useMemo(function () {
    return new Set(applicationProducts);
  }, [applicationProducts]);
  var applicationPermissionsSet = useMemo(function () {
    return new Set(applicationPermissions);
  }, [applicationPermissions]);
  var applicationPermissionsLength = useMemo(function () {
    return applicationPermissions.length;
  }, [applicationPermissions]);
  var isSuperAdminUser = useMemo(function () {
    return applicationRolesSet.has('ROLE_SUPERADMIN');
  }, [applicationRolesSet]);
  return /*#__PURE__*/_jsx(Context.Provider, {
    value: {
      applicationRolesSet: applicationRolesSet,
      applicationProductsSet: applicationProductsSet,
      applicationPermissionsSet: applicationPermissionsSet,
      applicationPermissionsLength: applicationPermissionsLength,
      isSuperAdminUser: isSuperAdminUser
    },
    children: children
  });
};
export var useRBACContext = function useRBACContext() {
  var context = useContext(Context);
  if (!context) {
    throw new Error('useRBACContext must be used within a RBACProvider');
  }
  return context;
};